// AI Automation control center — a single Settings card that surfaces
// every workflow the operator has configured, grouped by trigger type
// (welcome / draft reminder / birthday / anniversary / save), with
// inline toggles + timing controls. Deeper editing (message body, step
// order, wait times between steps) still lives in the Workflows tab —
// this is the "did I turn it on?" / "how many days before?" surface
// so operators don't have to dig.
//
// Reads/writes public.workflows directly. Mirrors the same patterns
// WorkflowsView uses (.active toggle, .lead_days_before, .milestone_days)
// so the two surfaces stay consistent — flipping a toggle here is the
// same as flipping it there.

import React from 'react';
import { supabase } from '../lib/supabase';

// Display config per trigger. Order matters — driver this list to
// reorder the panel rows. "anniversary" is a policy_milestone with
// milestone_days >= 300 — UX trick, not a separate trigger type, so
// the cron + schema don't need changes.
const TRIGGER_META = [
  {
    key:        'welcome',
    triggerDb:  'policy_milestone',
    test:       (w) => w.trigger === 'policy_milestone' && (w.milestone_days ?? 0) < 300,
    label:      'New-client welcome',
    icon:       '👋',
    desc:       'Fires N days after a policy is issued. Sets the relationship — short, warm, no upsell.',
    timingKey:  'milestone_days',
    timingLabel:'days after issue',
    timingMin:  1,
    timingMax:  300,
  },
  {
    key:        'payment_reminder',
    triggerDb:  'payment_reminder',
    test:       (w) => w.trigger === 'payment_reminder',
    label:      'Draft date reminder',
    icon:       '💳',
    desc:       'Fires before the client\'s monthly draft so they have heads-up + can fix banking before the charge.',
    timingKey:  'lead_days_before',
    timingLabel:'days before draft',
    timingMin:  1,
    timingMax:  30,
  },
  {
    key:        'birthday',
    triggerDb:  'birthday',
    test:       (w) => w.trigger === 'birthday',
    label:      'Birthday touch',
    icon:       '🎂',
    desc:       'Relationship moment, not a sales touch. Fires N days before (0 = on the day).',
    timingKey:  'lead_days_before',
    timingLabel:'days before birthday',
    timingMin:  0,
    timingMax:  30,
  },
  {
    key:        'anniversary',
    triggerDb:  'policy_milestone',
    test:       (w) => w.trigger === 'policy_milestone' && (w.milestone_days ?? 0) >= 300,
    label:      'Policy anniversary',
    icon:       '🎉',
    desc:       'Fires on the 1-year (or N-year) mark. Top-of-mind for referrals + cross-sells. Set milestone days to 365 for annual, 730 for 2-year, etc.',
    timingKey:  'milestone_days',
    timingLabel:'days after issue',
    timingMin:  300,
    timingMax:  3650,
  },
  {
    key:        'conservation',
    triggerDb:  'conservation_event',
    test:       (w) => w.trigger === 'conservation_event',
    label:      'Save lapsing policy',
    icon:       '🛟',
    desc:       'Fires on a parsed conservation event (lapse warning, NSF, grace period). Customer-success voice, not sales.',
    timingKey:  null,                     // no operator-tunable timing — fires on event
    timingLabel:'fires on event',
    timingMin:  null,
    timingMax:  null,
  },
];

export default function AutomationControlPanel({ currentUser, onGotoTab = () => {}, showToast = () => {}, settings = {}, setSettings = () => {} }) {
  const [workflows, setWorkflows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);
  // A2P 10DLC status — drives the "automations queued vs. live" framing
  // per the 2026-06-25 audit response. The tenant_a2p_status view returns
  // sms_ready=true when EITHER the per-tenant campaign is approved OR
  // the platform's shared fallback is wired. When false, every "Off"
  // toggle reads as "Queued — fires when A2P clears" instead of "the
  // operator hasn't bothered to flip me on" — which is what the audit
  // caught as the platform's default-OFF impression.
  const [a2pReady, setA2pReady] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('workflows')
      .select('id, name, active, trigger, lead_days_before, milestone_days, created_at')
      .order('created_at', { ascending: false });
    setWorkflows(Array.isArray(data) ? data : []);
    // Best-effort A2P status — view may be missing on dev installs.
    try {
      const { data: a2p } = await supabase
        .from('tenant_a2p_status')
        .select('sms_ready')
        .eq('owner_id', currentUser.id)
        .maybeSingle();
      setA2pReady(Boolean(a2p?.sms_ready));
    } catch { setA2pReady(null); }
    setLoading(false);
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  const toggleActive = async (w) => {
    setBusyId(w.id);
    const next = !w.active;
    const { error } = await supabase.from('workflows').update({ active: next }).eq('id', w.id);
    setBusyId(null);
    if (error) { showToast(`Couldn't update: ${error.message}`, 'error'); return; }
    setWorkflows(prev => prev.map(x => x.id === w.id ? { ...x, active: next } : x));
    showToast(next ? `${w.name} activated.` : `${w.name} paused.`);
  };

  const setTiming = async (w, key, value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    setBusyId(w.id);
    const { error } = await supabase.from('workflows').update({ [key]: n }).eq('id', w.id);
    setBusyId(null);
    if (error) { showToast(`Couldn't update timing: ${error.message}`, 'error'); return; }
    setWorkflows(prev => prev.map(x => x.id === w.id ? { ...x, [key]: n } : x));
  };

  if (loading) return null;

  // Group existing workflows by their meta bucket.
  const grouped = TRIGGER_META.map(meta => ({
    meta,
    workflows: workflows.filter(w => meta.test(w)),
  }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <h3 className="font-bold">AI automation control center</h3>
        <button
          onClick={() => onGotoTab('workflows')}
          className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold"
        >
          Open Workflows tab to edit messages →
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-2">
        Toggle each automation on or off, and tune the timing. Message body, multi-step drips, and template variables live in the Workflows tab.
      </p>
      {a2pReady === false && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-900 leading-snug">
          <strong>Heads up:</strong> A2P 10DLC is still pending. Toggling an automation ON queues it; nothing will actually send until the brand+campaign clear (usually 2 days after submit). You can keep tuning timing safely — the dispatch path holds.
          <button
            onClick={() => onGotoTab('settings')}
            className="ml-2 underline font-semibold hover:no-underline"
          >
            Check A2P status →
          </button>
        </div>
      )}
      {a2pReady === true && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-xs text-emerald-900 leading-snug">
          <strong>A2P ready.</strong> Anything you turn On below starts sending on its next scheduled fire.
        </div>
      )}

      <div className="space-y-3">
        {grouped.map(({ meta, workflows: matches }) => (
          <div key={meta.key} className="border border-slate-200 rounded-lg">
            <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="text-2xl">{meta.icon}</span>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{meta.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-snug">{meta.desc}</div>
                </div>
              </div>
              {matches.length === 0 && (
                <button
                  onClick={() => onGotoTab('workflows')}
                  className="text-xs font-bold uppercase tracking-tight bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-1.5 rounded-md"
                  title="No workflow set up yet — create one in the Workflows tab"
                >
                  Set up
                </button>
              )}
            </div>
            {matches.length > 0 && (
              <div className="border-t border-slate-100 divide-y divide-slate-100">
                {matches.map(w => (
                  <div key={w.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">{w.name}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 flex-wrap">
                      {meta.timingKey && (
                        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
                          <input
                            type="number"
                            min={meta.timingMin}
                            max={meta.timingMax}
                            value={w[meta.timingKey] ?? ''}
                            onBlur={(e) => {
                              if (Number(e.target.value) !== (w[meta.timingKey] || 0)) {
                                setTiming(w, meta.timingKey, e.target.value);
                              }
                            }}
                            onChange={(e) => {
                              setWorkflows(prev => prev.map(x => x.id === w.id ? { ...x, [meta.timingKey]: Number(e.target.value) || 0 } : x));
                            }}
                            className="w-16 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
                          />
                          <span>{meta.timingLabel}</span>
                        </label>
                      )}
                      {!meta.timingKey && (
                        <span className="text-xs text-slate-500 italic">{meta.timingLabel}</span>
                      )}
                      <button
                        onClick={() => toggleActive(w)}
                        disabled={busyId === w.id}
                        title={
                          w.active && a2pReady === false ? 'Queued — fires when A2P 10DLC clears'
                          : w.active                     ? 'Active — fires on next scheduled run'
                          :                                'Paused — flip On to queue'
                        }
                        className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-full disabled:opacity-50 ${
                          w.active && a2pReady === false
                            ? 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                            : w.active
                              ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                              : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                        }`}
                      >
                        {w.active && a2pReady === false ? 'Queued' : w.active ? 'On' : 'Off'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI agents driven by user_state settings (not the workflows
          table) live below the workflow-driven section so they're
          visually distinct. Each one is a single toggle + a couple
          of timing knobs. */}
      <div className="mt-4 border-t border-slate-200 pt-4">
        <RequirementsAutomationRow
          settings={settings}
          setSettings={setSettings}
        />
      </div>

      <div className="mt-4 text-xs text-slate-500 leading-relaxed">
        Heads-up: turning a workflow automation On only does anything if it has at least one message step. Open the workflow in the Workflows tab to add or edit messages.
      </div>
    </div>
  );
}

// Outstanding-requirements AI agent. Reads/writes user_state.cfg_settings_v1
// fields the api/workflow/scan-requirements cron consults:
//   - aiAutoRequirementsFollowup       boolean (default true)
//   - requirementsFollowupGapDays      integer (default 3)
//   - requirementsWarnAfterDays        integer (default 7)
function RequirementsAutomationRow({ settings, setSettings }) {
  const enabled = settings.aiAutoRequirementsFollowup !== false; // default ON
  const gapDays  = Number(settings.requirementsFollowupGapDays) > 0 ? Number(settings.requirementsFollowupGapDays) : 3;
  const warnDays = Number(settings.requirementsWarnAfterDays)   > 0 ? Number(settings.requirementsWarnAfterDays)   : 7;

  const toggle = () => setSettings({ ...settings, aiAutoRequirementsFollowup: !enabled });
  const setGap  = (n) => setSettings({ ...settings, requirementsFollowupGapDays: Math.max(1, Math.min(30, Number(n) || 3)) });
  const setWarn = (n) => setSettings({ ...settings, requirementsWarnAfterDays:   Math.max(1, Math.min(60, Number(n) || 7)) });

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="text-2xl">📋</span>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900">Outstanding requirements follow-up</div>
            <div className="text-xs text-slate-500 mt-0.5 leading-snug">
              AI texts + emails the client every {gapDays}d while items stay pending. Creates a follow-up task for you if anything goes {warnDays}+ days unresolved. Edit per-client items on each client.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          <label className="inline-flex items-center gap-1 text-xs text-slate-600">
            <input
              type="number" min={1} max={30}
              value={gapDays}
              onChange={(e) => setGap(e.target.value)}
              className="w-14 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
            />
            <span>days between reminders</span>
          </label>
          <label className="inline-flex items-center gap-1 text-xs text-slate-600">
            <input
              type="number" min={1} max={60}
              value={warnDays}
              onChange={(e) => setWarn(e.target.value)}
              className="w-14 border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
            />
            <span>days until follow-up task</span>
          </label>
          <button
            onClick={toggle}
            className={`text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-full ${
              enabled
                ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
            }`}
          >
            {enabled ? 'On' : 'Off'}
          </button>
        </div>
      </div>
    </div>
  );
}
